import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [info, setInfo] = useState({});
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); return; }
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setInfo(data); setValid(true); })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data);
      toast.success("Password set — welcome to Cake Society!");
      nav("/creator/edit");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return <div data-testid="set-password-loading" className="pt-32 pb-24 min-h-screen text-center text-stone-600">Checking your invite…</div>;
  }

  if (!valid) {
    return (
      <div data-testid="set-password-invalid" className="pt-32 pb-24 min-h-screen fade-in">
        <div className="max-w-md mx-auto px-6 text-center">
          <h1 className="font-serif text-5xl text-stone-900">Link expired</h1>
          <p className="mt-3 text-stone-600">This invite link is invalid or has expired. Please ask the studio to resend your invitation.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="set-password-page" className="pt-32 pb-24 min-h-screen fade-in">
      <div className="max-w-md mx-auto px-6">
        <h1 className="font-serif text-5xl text-stone-900">Set your password.</h1>
        <p className="mt-2 text-stone-600">Welcome, {info.name || info.email}! Create a password to access your creator account.</p>
        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-2">New password</label>
            <input
              data-testid="set-password-input"
              type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#121212] text-[#121212] focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-2">Confirm password</label>
            <input
              data-testid="set-password-confirm"
              type="password" required
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#121212] text-[#121212] focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
            />
          </div>
          <button
            data-testid="set-password-submit"
            disabled={busy}
            className="w-full px-6 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40 transition-all"
          >
            {busy ? "Setting password…" : "Set password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
